import { get, onValue, ref, remove, update, set } from "firebase/database";
import { v4 as generateUuid } from "uuid";
import { StoreService } from "./StoreService";
import { db } from "@/firebase/firebase";
import { IAssignment, IAssignmentPrompt } from "@/types/AssignmentTypes";
import { IUserPlaylistData } from "@/types/UserDataTypes";
import { PlaylistService } from "./PlaylistService";
import { IPlaylistConfig } from "@/types/PlaylistTypes";
import { AssignmentTypes } from "@/components/assignments/assignmentConfigs";
import { Analytics } from "./Analytics";
import { config } from "@/config";

class AssignmentsService {
  /* ================================
   SUBSCRIBERS AND FETCHERS
  ================================ */
  subscribeToUserAssignments(callback: (assignments: any) => void) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return () => {}; // Return empty unsubscribe function

    const assignmentsRef = ref(db, `userAssignments/${activeUser.id}`);
    return onValue(assignmentsRef, (snapshot) => {
      const assignments = snapshot.val();
      callback(assignments || []);
    });
  }

  subscribeToFullAssignment(
    assignmentId: string,
    callback: (assignment: any) => void,
  ) {
    const assignmentRef = ref(db, `assignments/${assignmentId}`);
    return onValue(
      assignmentRef,
      (snapshot) => {
        const assignment = snapshot.val();
        callback(assignment);
      },
      (error) => {
        console.error("error subscribing to full assignment", error);
      },
    );
  }

  subscribeToUserAssignmentResponses({
    assignmentId,
    userId,
    callback,
  }: {
    assignmentId: string;
    userId: string;
    callback: (responses: any) => void;
  }) {
    const userResponsesRef = ref(
      db,
      `assignmentResponses/${assignmentId}/${userId}/responses`,
    );
    return onValue(userResponsesRef, (snapshot) => {
      const responses = snapshot.val();
      callback(responses);
    });
  }

  async fetchUserAssignmentResults({
    assignmentId,
    studentUserId,
    ignoreEmails = false,
  }: {
    assignmentId: string;
    studentUserId: string;
    ignoreEmails?: boolean;
  }) {
    const userEmailRef = ref(db, `users/${studentUserId}/email`);
    const userNameRef = ref(db, `users/${studentUserId}/name`);
    const userPlaylistsRef = ref(db, `users/${studentUserId}/playlistData`);
    const userVideoDataRef = ref(db, `videoDataByUser/${studentUserId}`);
    const userResponsesRef = ref(
      db,
      `assignmentResponses/${assignmentId}/${studentUserId}/responses`,
    );

    async function getUserEmail() {
      if (ignoreEmails) return `${studentUserId.slice(0, 4)}@***.edu`;
      try {
        const userEmail = (await get(userEmailRef)).val();
        return userEmail;
      } catch (error) {
        console.error("error getting user email", error);
        return "";
      }
    }

    async function getUserName() {
      try {
        const userName = (await get(userNameRef)).val();
        return userName;
      } catch (error) {
        console.error("error getting user name", error);
        return "";
      }
    }

    async function getUserPlaylistData() {
      try {
        const userPlaylistData = (await get(userPlaylistsRef)).val();
        return userPlaylistData;
      } catch (error) {
        console.error("error getting user playlist data", error);
        return {};
      }
    }

    async function getUserVideoData() {
      try {
        const userVideoData = (await get(userVideoDataRef)).val();
        return userVideoData;
      } catch (error) {
        console.error("error getting user video data", error);
        return {};
      }
    }

    async function getUserResponses() {
      try {
        const userResponses = (await get(userResponsesRef)).val();
        return userResponses;
      } catch (error) {
        console.error("error getting user responses", error);
        return {};
      }
    }

    // read val once
    const promises = [
      getUserEmail(),
      getUserName(),
      getUserPlaylistData(),
      getUserVideoData(),
      getUserResponses(),
    ];
    const [
      userEmail,
      userName,
      userPlaylistData,
      userVideoData,
      userResponses,
    ] = await Promise.all(promises);

    return {
      email: userEmail,
      name: userName,
      assignmentId,
      userId: studentUserId,
      playlistData: userPlaylistData,
      videoData: userVideoData,
      responses: userResponses,
    };
    // const assignmentRef = ref(db, `assignments/${assignmentId}/results/${studentUserId}`);
  }

  async fetchMultipleUserAssignmentResults({
    assignmentId,
    studentIdsFromAssignment,
    ignoreEmails = false,
  }: {
    assignmentId: string;
    studentIdsFromAssignment: string[];
    ignoreEmails?: boolean;
  }) {
    const promises = studentIdsFromAssignment.map((studentUserId) => {
      return this.fetchUserAssignmentResults({
        assignmentId,
        studentUserId,
        ignoreEmails,
      });
    });
    const results = (await Promise.all(promises))
      .filter((result) => !!result.email)
      .sort((a, b) => {
        return (a.email || "zzz").localeCompare(b.email || "zzz");
      });
    return results;
  }

  /* ================================
  ACTIONS
  ================================ */
  async createNewBlankAssignment({
    defaultAssignmentPromptConfigs,
  }: {
    defaultAssignmentPromptConfigs: Partial<IAssignmentPrompt>[];
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return null;
    const partnerSlug =
      activeUser?.partnerData?.partnerSlug || activeUser?.partnerSlug || null;

    const uuid = generateUuid();
    const assignmentRef = ref(db, `assignments/${uuid}`);
    const userAssignmentsRef = ref(
      db,
      `userAssignments/${activeUser.id}/${uuid}`,
    );

    const baseAssignmentData = {
      id: uuid,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      status: "public",
      partnerSlug,
      assignmentType: "anyAdvizers",
      title: AssignmentTypes.anyAdvizers.defaultTitle,
      instructions: AssignmentTypes.anyAdvizers.defaultInstructions,
      requiredCount: AssignmentTypes.anyAdvizers.defaultRequiredCount || null,
      totalMinutesToComplete: 30,
    };

    await set(userAssignmentsRef, baseAssignmentData);
    await set(assignmentRef, {
      playlists: {},
      // advizers: {},
      videos: {},
      users: {
        [activeUser.id]: {
          role: "owner",
        },
      },
      ...baseAssignmentData,
    });

    for (const prompt of defaultAssignmentPromptConfigs) {
      this.addPromptToAssignment({
        assignmentId: uuid,
        promptData: prompt,
      });
    }

    Analytics.assignmentCreate({
      assignmentId: uuid,
    });

    return uuid;
  }

  updateAssignment({
    assignmentId,
    data,
    shouldUpdateUserAssignment,
  }: {
    assignmentId: string;
    data: any;
    shouldUpdateUserAssignment?: boolean;
  }) {
    const activeUser = StoreService.getActiveUser();
    if (!activeUser) return;

    const assignmentRef = ref(db, `assignments/${assignmentId}`);
    update(assignmentRef, data);

    if (shouldUpdateUserAssignment) {
      const userAssignmentsRef = ref(
        db,
        `userAssignments/${activeUser.id}/${assignmentId}`,
      );
      update(userAssignmentsRef, data);
    }
  }

  addPlaylistToAssignment({
    assignmentId,
    playlistId,
  }: {
    assignmentId: string;
    playlistId: string;
  }) {
    const assignmentRef = ref(
      db,
      `assignments/${assignmentId}/playlists/${playlistId}`,
    );

    update(assignmentRef, {
      id: playlistId,
      updatedAt: Date.now(),
    });

    const playlist = StoreService.getPlaylistById(playlistId);
    Analytics.assignmentAddPlaylist({
      assignmentId,
      playlistId,
      playlistTitle: playlist?.title,
    });
  }

  removePlaylistFromAssignment({
    assignmentId,
    playlistId,
  }: {
    assignmentId: string;
    playlistId: string;
  }) {
    const assignmentRef = ref(
      db,
      `assignments/${assignmentId}/playlists/${playlistId}`,
    );
    remove(assignmentRef);

    const playlist = StoreService.getPlaylistById(playlistId);
    Analytics.assignmentRemovePlaylist({
      assignmentId,
      playlistId,
      playlistTitle: playlist?.title,
    });
  }

  updateAssignmentTitle({
    assignmentId,
    title,
  }: {
    assignmentId: string;
    title: string;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { title },
      shouldUpdateUserAssignment: true,
    });

    Analytics.assignmentUpdateTitle({
      assignmentId,
      inputValue: title,
    });
  }

  updateAssignmentContactEmail({
    assignmentId,
    contactEmail,
  }: {
    assignmentId: string;
    contactEmail: string;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { contactEmail },
    });
  }

  updateAssignmentType({
    assignmentId,
    assignmentType,
  }: {
    assignmentId: string;
    assignmentType: string;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { assignmentType },
    });

    Analytics.assignmentUpdateType({
      assignmentId,
      assignmentType,
    });
  }

  updateAssignmentUploadType({
    assignmentId,
    uploadType,
  }: {
    assignmentId: string;
    uploadType: "careerLeader" | "custom" | null;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { uploadType },
    });
  }

  updateAssignmentInstructions({
    assignmentId,
    instructions,
  }: {
    assignmentId: string;
    instructions: string;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { instructions },
    });

    Analytics.assignmentUpdateInstructions({
      assignmentId,
      inputValue: instructions,
    });
  }

  updateAssignmentCount({
    assignmentId,
    count,
    doNotTrack,
  }: {
    assignmentId: string;
    count: number;
    doNotTrack?: boolean;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { requiredCount: count },
    });

    if (doNotTrack) return;

    Analytics.assignmentUpdateCount({
      assignmentId,
      requiredCount: count,
    });
  }

  updateAssignmentTotalMinutesToComplete({
    assignmentId,
    totalMinutesToComplete,
  }: {
    assignmentId: string;
    totalMinutesToComplete: number | null;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { totalMinutesToComplete: totalMinutesToComplete || 0 },
    });

    Analytics.assignmentUpdateTotalMinutesToComplete({
      assignmentId,
      value: totalMinutesToComplete || 0,
    });
  }

  updateAssignmentSessionSchedulingLink({
    assignmentId,
    sessionSchedulingLink,
  }: {
    assignmentId: string;
    sessionSchedulingLink: string | null;
  }) {
    this.updateAssignment({
      assignmentId,
      data: { sessionSchedulingLink },
    });
  }

  async addStudentToAssignment({
    studentUserId,
    assignmentId,
    assignment,
  }: {
    studentUserId: string;
    assignmentId: string;
    assignment?: IAssignment;
  }) {
    const userAssignmentRef = ref(
      db,
      `userAssignments/${studentUserId}/${assignmentId}`,
    );

    update(userAssignmentRef, {
      lastVisitedAt: Date.now(),
    });

    const assignmentRef = ref(
      db,
      `assignments/${assignmentId}/users/${studentUserId}`,
    );

    const currentValue = (await get(assignmentRef)).val();
    if (currentValue?.role) return;

    update(assignmentRef, {
      role: "student",
    });

    Analytics.assignmentAddStudent({
      assignmentId,
      assignmentTitle: assignment?.title,
    });
  }

  async handleUserConsentReject({
    studentUserId,
    assignment,
  }: {
    studentUserId: string;
    assignment: IAssignment;
  }) {
    // remove from user assignments
    const userAssignmentsRef = ref(
      db,
      `userAssignments/${studentUserId}/${assignment.id}`,
    );
    remove(userAssignmentsRef);

    // remove user from assignment
    const assignmentRef = ref(
      db,
      `assignments/${assignment.id}/users/${studentUserId}`,
    );
    remove(assignmentRef);

    Analytics.assignmentUserConsentReject({
      assignmentId: assignment?.id || "",
      assignmentTitle: assignment?.title || "",
    });
  }

  updateUserAssignmentConsent({
    assignment,
    studentUserId,
  }: {
    assignment: IAssignment;
    studentUserId: string;
  }) {
    const ownerIds = Object.keys(assignment.users || {}).filter(
      (userId) => assignment.users?.[userId]?.role === "owner",
    );
    for (const ownerId of ownerIds) {
      const userConsentRef = ref(
        db,
        `userToUserConsent/${studentUserId}/${ownerId}`,
      );
      update(userConsentRef, { consent: Date.now() });
    }

    const userAssignmentConsentRef = ref(
      db,
      `userAssignments/${studentUserId}/${assignment.id}`,
    );

    update(userAssignmentConsentRef, {
      consent: Date.now(),
    });

    Analytics.assignmentUserConsentAccept({
      assignmentId: assignment.id,
      assignmentTitle: assignment.title,
    });
  }

  addPromptToAssignment({
    assignmentId,
    promptData,
  }: {
    assignmentId: string;
    promptData: Partial<IAssignmentPrompt>;
  }) {
    const id = generateUuid();
    const assignmentRef = ref(db, `assignments/${assignmentId}/prompts/${id}`);

    update(assignmentRef, {
      minCharacters: null,
      maxCharacters: null,
      ...promptData,
      id,
      createdAt: Date.now(),
    });

    Analytics.assignmentAddPrompt({
      assignmentId,
      assignmentPromptKey: promptData.key,
      assignmentPrompt: promptData.prompt,
    });

    return id;
  }

  updateCustomPrompt({
    assignmentId,
    promptId,
    prompt,
  }: {
    assignmentId: string;
    promptId: string;
    prompt: string;
  }) {
    const assignmentRef = ref(
      db,
      `assignments/${assignmentId}/prompts/${promptId}`,
    );
    update(assignmentRef, { prompt });
  }

  addCustomPromptToAssignment({ assignmentId }: { assignmentId: string }) {
    this.addPromptToAssignment({
      assignmentId,
      promptData: {
        key: "custom",
        prompt: "",
      },
    });
  }

  removePromptFromAssignment({
    assignmentId,
    promptId,
    promptData,
  }: {
    assignmentId: string;
    promptId: string;
    promptData: IAssignmentPrompt;
  }) {
    const assignmentRef = ref(
      db,
      `assignments/${assignmentId}/prompts/${promptId}`,
    );
    remove(assignmentRef);

    Analytics.assignmentRemovePrompt({
      assignmentId,
      assignmentPromptKey: promptData.key,
      assignmentPrompt: promptData.prompt,
    });
  }

  updateUserAssignmentResponse({
    assignmentId,
    studentUserId,
    promptId,
    response,
  }: {
    assignmentId: string;
    studentUserId: string;
    promptId: string;
    response: string;
  }) {
    const assignmentResponseRef = ref(
      db,
      `assignmentResponses/${assignmentId}/${studentUserId}/responses/${promptId}`,
    );
    update(assignmentResponseRef, {
      response,
      lastUpdatedAt: Date.now(),
    });

    Analytics.assignmentUpdateStudentResponse({
      assignmentId,
      label: promptId,
      inputValue: response,
    });
  }

  removeAssignment({ assignmentId }: { assignmentId: string }) {
    const assignmentRef = ref(db, `assignments/${assignmentId}`);
    update(assignmentRef, {
      removed: Date.now(),
    });
  }
  /* ================================
  HELPER FUNCTIONS
  ================================ */
  getStudentIdsFromAssignment(assignment: IAssignment) {
    return Object.entries(assignment?.users || {})
      .filter(([, user]) => user?.role === "student")
      .map(([userId]) => userId);
  }

  calculateAssignmentPointsForAdvizersAssignment({
    totalMinutesToComplete,
    requiredAdvizersCount = 1,
    userTotalAdvizersWatched,
    userTotalMinutesWatched,
    totalCompleteResponses,
    totalRequiredResponses,
  }: {
    totalMinutesToComplete: number;
    requiredAdvizersCount: number;
    userTotalAdvizersWatched: number;
    userTotalMinutesWatched: number;
    totalCompleteResponses: number;
    totalRequiredResponses: number;
  }) {
    const totalPossiblePointsForVideos = config.totalPossiblePointsForVideos;
    const totalPossiblePointsForAdvizersWatched = Math.max(
      1,
      requiredAdvizersCount,
    );
    const totalPossiblePointsForMinutesWatched =
      totalPossiblePointsForVideos - totalPossiblePointsForAdvizersWatched;

    const fractionOfMinutesWatched = Math.min(
      userTotalMinutesWatched / totalMinutesToComplete,
      1,
    );

    const fractionOfAdvizersWatched = Math.min(
      userTotalAdvizersWatched / requiredAdvizersCount,
      1,
    );

    const totalUserPointsForMinutesWatched =
      totalPossiblePointsForMinutesWatched * fractionOfMinutesWatched;

    const totalUserPointsForAdvizersWatched = Math.round(
      totalPossiblePointsForAdvizersWatched * fractionOfAdvizersWatched,
    );

    const totalUserPointsForVideos =
      totalUserPointsForMinutesWatched + totalUserPointsForAdvizersWatched;

    const totalPossiblePointsForResponses =
      config.totalPossiblePointsForResponses;

    const totalUserPointsForResponses =
      totalPossiblePointsForResponses *
      (totalCompleteResponses / totalRequiredResponses);

    const totalPossiblePoints =
      totalPossiblePointsForVideos + totalPossiblePointsForResponses;

    const totalUserPoints =
      totalUserPointsForVideos + totalUserPointsForResponses;

    const userPercentOfPoints = (totalUserPoints / totalPossiblePoints) * 100;

    const results = {
      // totalMinutesToComplete,
      // requiredAdvizersCount,
      // userTotalAdvizersWatched,
      // userTotalMinutesWatched,
      // totalPossiblePointsForVideos,
      // totalPossiblePointsForResponses,
      // percentOfMinutesWatched: fractionOfMinutesWatched * 100,
      // totalUserPointsForMinutesWatched,
      // totalUserPointsForAdvizersWatched,
      // totalUserPointsForVideos,
      // totalUserPointsForResponses,
      // totalRequiredResponses,
      // totalCompleteResponses,
      totalUserPoints,
      totalPossiblePoints,
      userPercentOfPoints,
    };

    // console.log("Results", results);

    return results;
  }

  getAssignmentResultStatus(params: {
    assignment: IAssignment;
    userPlaylistData?: IUserPlaylistData;
    userVideoData?: any;
    advizerMap?: any;
    userVideosWatched: any[];
    userResponses: any;
    assignmentPrompts?: Record<string, IAssignmentPrompt>;
  }) {
    const {
      assignment,
      userPlaylistData,
      userVideoData,
      advizerMap,
      userVideosWatched,
      userResponses,
    } = params;

    const assignmentPrompts =
      assignment?.prompts || params.assignmentPrompts || {};

    const results: {
      status: "notStarted" | "inProgress" | "completed";
      isAllAssignmentPlaylistsCompleted?: boolean;
      isAssignmentInProgress?: boolean;
      inProgressAssignmentPlaylistKeys?: string[];
      completedAssignmentPlaylistKeys?: string[];
      assignmentPlaylistKeys?: string[];
      userCompletedPlaylistKeys?: string[];
      inProgressPlaylistKeys?: string[];
      isAllAssignmentPromptsCompleted?: boolean;
      userResponses?: any;
      isVideoWatchingCompleted?: boolean;
      didWatchEnoughAdvizers?: boolean;
      minutesLeftToWatch?: number;
      responsesLeftToComplete?: number;
      percentOfContentCompleted?: number;
      percentOfResponsesCompleted?: number;
      points: ReturnType<
        typeof AssignmentService.calculateAssignmentPointsForAdvizersAssignment
      >;
    } = {
      status: "notStarted",
      isAllAssignmentPlaylistsCompleted: false,
      isAssignmentInProgress: false,
      inProgressAssignmentPlaylistKeys: [],
      completedAssignmentPlaylistKeys: [],
      assignmentPlaylistKeys: [],
      userCompletedPlaylistKeys: [],
      inProgressPlaylistKeys: [],
      isAllAssignmentPromptsCompleted: false,
      userResponses: {},
      isVideoWatchingCompleted: false,
      didWatchEnoughAdvizers: false,
      minutesLeftToWatch: 0,
      responsesLeftToComplete: 0,
      points: {
        totalPossiblePoints: 0,
        totalUserPoints: 0,
        userPercentOfPoints: 0,
      },
    };

    const {
      didUserCompleteAllAssignmentPrompts,
      totalCompleteResponses,
      totalRequiredResponses,
    } =
      assignmentPrompts || assignment?.prompts
        ? this.checkDidUserCompleteAllAssignmentPrompts({
            assignmentPrompts,
            userResponses,
          })
        : {
            didUserCompleteAllAssignmentPrompts: true,
            totalCompleteResponses: 0,
            totalRequiredResponses: 0,
          };

    const isAllAssignmentPromptsCompleted =
      didUserCompleteAllAssignmentPrompts &&
      totalCompleteResponses === totalRequiredResponses;

    results.isAllAssignmentPromptsCompleted =
      didUserCompleteAllAssignmentPrompts;

    results.userResponses = userResponses;
    results.responsesLeftToComplete = this.getAssignmentPromptsLeftToComplete({
      assignmentPrompts,
      userResponses,
    });

    const didGetRecsFromUpload = assignment?.uploadType
      ? !!userResponses?.careerLeader?.response
      : true;

    if (assignment?.assignmentType === "anyAdvizers") {
      const userRankedAdvizersWatchedData =
        this.getUserRankedAdvizersWatchedData({
          advizerMap: advizerMap,
          userVideosWatched: userVideosWatched,
          userVideoData: userVideoData?.videos || userVideoData,
        });

      const completedAdvizers = userRankedAdvizersWatchedData.filter(
        (advizer) =>
          this.checkIsAdvizerCompletedForAssignment({
            advizerId: advizer.id,
            rankedAdvizerWatchDataForSingleAdvizer: advizer,
          }),
      );

      const totalMinutesWatched = userRankedAdvizersWatchedData.reduce(
        (acc, advizer) => acc + advizer.totalSecondsWatched / 60,
        0,
      );

      const didWatchTotalMinutes =
        totalMinutesWatched >= assignment?.totalMinutesToComplete;
      const hasTotalMinutesToComplete = !!assignment?.totalMinutesToComplete;

      const watchedAdvizersCount = userRankedAdvizersWatchedData.length;
      const didWatchEnoughAdvizers =
        watchedAdvizersCount >= (assignment?.requiredCount || 1);

      results.didWatchEnoughAdvizers = didWatchEnoughAdvizers;
      results.minutesLeftToWatch =
        assignment?.totalMinutesToComplete &&
        Math.max(0, assignment?.totalMinutesToComplete - totalMinutesWatched);

      results.points = this.calculateAssignmentPointsForAdvizersAssignment({
        totalMinutesToComplete: assignment?.totalMinutesToComplete,
        requiredAdvizersCount: assignment?.requiredCount,
        userTotalAdvizersWatched: watchedAdvizersCount,
        userTotalMinutesWatched: totalMinutesWatched,
        totalCompleteResponses,
        totalRequiredResponses,
      });

      if (
        isAllAssignmentPromptsCompleted &&
        didWatchEnoughAdvizers &&
        didGetRecsFromUpload &&
        hasTotalMinutesToComplete &&
        didWatchTotalMinutes
      ) {
        results.status = "completed";
        // return results;
      } else if (
        isAllAssignmentPromptsCompleted &&
        didWatchEnoughAdvizers &&
        didGetRecsFromUpload &&
        completedAdvizers.length >= (assignment?.requiredCount || 1)
      ) {
        results.status = "completed";
        // return results;
      } else if (userRankedAdvizersWatchedData.length > 0) {
        results.status = "inProgress";
        // return results;
      }

      // results.status = "notStarted";
      return results;
    }

    if (!userPlaylistData || !assignment) {
      return results;
    }

    const userCompletedPlaylistKeys =
      PlaylistService.getUserCompletedPlaylistKeys({
        userPlaylistData,
      });

    const inProgressPlaylistKeys =
      PlaylistService.getUserInProgressPlaylistKeys({
        userPlaylistData,
      });

    if (assignment?.assignmentType === "anyPlaylists") {
      results.userCompletedPlaylistKeys = userCompletedPlaylistKeys;
      results.inProgressPlaylistKeys = inProgressPlaylistKeys;
      if (
        isAllAssignmentPromptsCompleted &&
        userCompletedPlaylistKeys.length >= (assignment?.requiredCount || 1)
      ) {
        results.status = "completed";
        // return results;
      } else if (
        inProgressPlaylistKeys.length > 0 ||
        userCompletedPlaylistKeys.length > 0
      ) {
        results.status = "inProgress";
      }
      return results;
    }

    const assignmentPlaylistKeys = Object.keys(assignment.playlists || {});

    const inProgressAssignmentPlaylistKeys = inProgressPlaylistKeys.filter(
      (playlistKey) => assignmentPlaylistKeys.includes(playlistKey),
    );
    const completedAssignmentPlaylistKeys = userCompletedPlaylistKeys.filter(
      (playlistKey) => assignmentPlaylistKeys.includes(playlistKey),
    );

    const isAllAssignmentPlaylistsCompleted = assignmentPlaylistKeys.every(
      (playlistKey) => userCompletedPlaylistKeys.includes(playlistKey),
    );

    const isAssignmentInProgress =
      inProgressAssignmentPlaylistKeys.length > 0 ||
      completedAssignmentPlaylistKeys.length > 0;

    results.isAllAssignmentPlaylistsCompleted =
      isAllAssignmentPlaylistsCompleted;
    results.isAssignmentInProgress = isAssignmentInProgress;
    results.inProgressAssignmentPlaylistKeys = inProgressAssignmentPlaylistKeys;
    results.completedAssignmentPlaylistKeys = completedAssignmentPlaylistKeys;
    results.assignmentPlaylistKeys = assignmentPlaylistKeys;

    if (isAllAssignmentPromptsCompleted && isAllAssignmentPlaylistsCompleted) {
      results.status = "completed";
      // return results;
    } else if (isAssignmentInProgress) {
      results.status = "inProgress";
      // return results;
    }

    return results;
  }

  getAssignmentPlaylists({
    assignment,
    playlists,
  }: {
    assignment: IAssignment;
    playlists: IPlaylistConfig[];
  }) {
    if (!assignment) return [];
    return Object.keys(assignment.playlists || {})
      .map((playlistId) => {
        return {
          ...playlists.find((playlist) => playlist.id === playlistId),
          ...assignment.playlists[playlistId],
        };
      })
      .filter((playlist) => !!playlist)
      .sort((a, b) => {
        return (a.updatedAt || 0) - (b.updatedAt || 0);
      });
  }

  checkIfUserIsAssignmentOwner({
    assignment,
    userId,
  }: {
    assignment: IAssignment;
    userId: string;
  }) {
    return assignment.users?.[userId]?.role === "owner";
  }

  getUserVideosWatched({
    videoMap,
    userVideoData,
  }: {
    videoMap: any;
    userVideoData: any;
  }) {
    const videosWatched = Object.keys(userVideoData || {})
      .filter((vimeoId) => {
        return videoMap[vimeoId];
      })
      .map((vimeoId) => {
        return videoMap[vimeoId];
      });

    return videosWatched;
  }

  getUserAdvizersWatched({
    advizerMap,
    userVideosWatched,
  }: {
    advizerMap: any;
    userVideosWatched: any[];
  }) {
    const advizerIdsWatched = (userVideosWatched || []).map(
      (video) => video.advizerId,
    );
    return Array.from(new Set([...advizerIdsWatched]))
      .map((advizerId) => advizerMap[advizerId])
      .filter((advizer) => !!advizer);
  }

  getUserRankedAdvizersWatchedData({
    advizerMap,
    userVideosWatched,
    userVideoData,
  }: {
    advizerMap: any;
    userVideosWatched: any[];
    userVideoData: any;
  }) {
    const userAdvizersWatched = this.getUserAdvizersWatched({
      advizerMap,
      userVideosWatched,
    });

    return userAdvizersWatched
      .map((advizer) => {
        const videosWatched = userVideosWatched.filter(
          (video) => video.advizerId === advizer.id,
        );
        const totalSecondsWatched = videosWatched.reduce(
          (acc, video) =>
            acc +
            Math.min(
              video.duration,
              Math.max(
                userVideoData[video.vimeoId]?.secondsWatched || 0,
                userVideoData?.[video.vimeoId]?.totalSecondsWatched || 0,
              ),
            ),
          0,
        );

        const totalVideoDurationSeconds =
          advizer.interviews?.[0]?.videos?.reduce(
            (acc: number, video: any) => acc + (video.duration || 0),
            0,
          );

        return {
          id: advizer.id,
          advizer: advizer,
          videos: advizer?.videos.map((v: any) => ({
            ...v,
            userSecondsWatched: Math.min(
              v.duration,
              Math.max(
                userVideoData[v.vimeoId]?.secondsWatched || 0,
                userVideoData?.[v.vimeoId]?.totalSecondsWatched || 0,
              ),
            ),
            userTotalSecondsWatched:
              userVideoData[v.vimeoId]?.totalSecondsWatched || 0,
            rawViewingData: userVideoData[v.vimeoId],
          })),
          totalSecondsWatched: totalSecondsWatched || 0,
          totalVideoDurationSeconds: totalVideoDurationSeconds || 0,
          // videosWatched: videosWatched.map((video) => ({
          //   ...video,
          //   viewingData: userVideoData[video.vimeoId],
          // })),
        };
      })
      .sort((a, b) => b.totalSecondsWatched - a.totalSecondsWatched);
  }

  getUserRankedPlaylistsWatchedData({
    playlistLibrary,
    userPlaylistData,
  }: {
    playlistLibrary: any;
    userPlaylistData: IUserPlaylistData;
  }) {
    const userCompletedPlaylistKeys =
      PlaylistService.getUserCompletedPlaylistKeys({
        userPlaylistData,
      });

    const userInProgressPlaylistKeys =
      PlaylistService.getUserInProgressPlaylistKeys({
        userPlaylistData,
      });

    return [...userCompletedPlaylistKeys, ...userInProgressPlaylistKeys].map(
      (playlistId) => {
        const dataForPlaylist = userPlaylistData?.playlists?.[playlistId];
        const understandingResponseBefore =
          dataForPlaylist?.steps?.["understanding-response-before"]?.value;
        const understandingResponseAfter =
          dataForPlaylist?.steps?.["understanding-response-after"]?.value;

        let percentChangeInUnderstanding = null;
        if (understandingResponseBefore && understandingResponseAfter) {
          percentChangeInUnderstanding =
            ((understandingResponseAfter - understandingResponseBefore) /
              understandingResponseBefore) *
            100;
        }

        return {
          id: playlistId,
          playlist: playlistLibrary?.[playlistId],
          totalSecondsWatched: 0,
          totalVideoDurationSeconds: 0,
          understandingResponseBefore,
          understandingResponseAfter,
          percentChangeInUnderstanding,
        };
      },
    );
  }

  checkIsAdvizerCompletedForAssignment({
    rankedAdvizerWatchDataForSingleAdvizer,
  }: {
    advizerId: string;
    rankedAdvizerWatchDataForSingleAdvizer: any;
  }) {
    const { totalVideoDurationSeconds, totalSecondsWatched } =
      rankedAdvizerWatchDataForSingleAdvizer;
    const isCompleted = totalSecondsWatched / totalVideoDurationSeconds > 0.95;
    return isCompleted;
  }

  getArrayOfAssignmentPrompts({ assignment }: { assignment: IAssignment }) {
    return Object.values(assignment?.prompts || {});
  }

  checkDidUserCompleteAssignmentPrompt({
    responseDataForPrompt,
  }: {
    responseDataForPrompt: any;
  }) {
    return (
      !!responseDataForPrompt?.response?.trim() &&
      responseDataForPrompt?.response?.trim().length >=
        (config.minimumReflectionResponseCharacterLength || 1)
    );
  }

  checkDidUserCompleteAllAssignmentPrompts({
    assignmentPrompts,
    userResponses,
  }: {
    assignmentPrompts?: Record<string, IAssignmentPrompt>;
    userResponses: any;
  }) {
    if (!assignmentPrompts) {
      return {
        didUserCompleteAllAssignmentPrompts: true,
        totalCompleteResponses: 0,
        totalRequiredResponses: 0,
      };
    }

    const prompts = Object.values(assignmentPrompts);
    const totalRequiredResponses = prompts.length;
    let totalCompleteResponses = 0;

    prompts.forEach((prompt) => {
      const isComplete = this.checkDidUserCompleteAssignmentPrompt({
        responseDataForPrompt: userResponses?.[prompt.id],
      });
      if (isComplete) {
        totalCompleteResponses++;
      }
    });

    const didUserCompleteAllAssignmentPrompts =
      totalCompleteResponses === totalRequiredResponses;

    return {
      didUserCompleteAllAssignmentPrompts,
      totalCompleteResponses,
      totalRequiredResponses,
    };
  }

  getAssignmentPromptsLeftToComplete({
    assignmentPrompts,
    userResponses,
  }: {
    assignmentPrompts?: Record<string, IAssignmentPrompt>;
    userResponses: any;
  }) {
    if (!assignmentPrompts) return 0;
    return Object.values(assignmentPrompts).filter((prompt) => {
      return !this.checkDidUserCompleteAssignmentPrompt({
        responseDataForPrompt: userResponses?.[prompt.id],
      });
    }).length;
  }

  checkIsSomeAssignmentPromptStarted({
    assignmentPrompts,
    userResponses,
  }: {
    assignmentPrompts?: Record<string, IAssignmentPrompt>;
    userResponses: any;
  }) {
    if (!assignmentPrompts) return true;
    return Object.values(assignmentPrompts).some((prompt) => {
      return !!userResponses?.[prompt.id]?.response;
    });
  }

  async exportAllAssignmentResults({
    assignmentId,
    studentIdsFromAssignment,
    activeAssignment,
    videoMap,
    advizerMap,
    playlistLibrary,
    exportType = "all",
    isAdminExport = false,
  }: {
    assignmentId: string;
    studentIdsFromAssignment: string[];
    activeAssignment: IAssignment;
    videoMap: any;
    advizerMap: any;
    playlistLibrary: any;
    exportType?: "canvas" | "all";
    isAdminExport?: boolean;
  }) {
    const results = await this.fetchMultipleUserAssignmentResults({
      assignmentId,
      studentIdsFromAssignment,
      ignoreEmails: isAdminExport,
    });

    const exportResultsMap: Record<string, any> = {};

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const { email } = result;

      const userVideosWatched = this.getUserVideosWatched({
        videoMap,
        userVideoData: result?.videoData?.videos || {},
      });

      const mostRecentVideoWatched = Math.max(
        ...[
          ...Object.values(result?.videoData?.videos || {}).map(
            (video: any) => video.lastUpdatedAt,
          ),
          0,
        ],
      );

      let firstVideoWatched =
        Math.min(
          ...[
            ...Object.values(result?.videoData?.videos || {}).map(
              (video: any) => video.lastUpdatedAt,
            ),
          ],
        ) || 0;

      if (firstVideoWatched == Math.abs(Infinity)) {
        firstVideoWatched = 0;
      }

      const mostRecentAssignmentPromptResponse = Math.max(
        ...[
          ...Object.values(result?.responses || {}).map(
            (response: any) => response.lastUpdatedAt,
          ),
          0,
        ],
      );

      const { status: userAssignmentStatus, ...userResultData } =
        this.getAssignmentResultStatus({
          assignment: activeAssignment,
          userPlaylistData: result?.playlistData,
          userVideoData: result?.videoData?.videos || {},
          userVideosWatched,
          userResponses: result?.responses,
          advizerMap,
          assignmentPrompts: activeAssignment?.prompts,
        });

      console.log("EXPORT RESULT STATUS", email, userResultData);

      const formattedUserAssignmentStatusMap = {
        notStarted: "Not Started",
        inProgress: "In Progress",
        completed: "Completed",
      };

      const advizersTheyWatched = userVideosWatched
        .map((video) => {
          return advizerMap[video.advizerId];
        })
        .reduce((acc: Record<string, any>, advizer) => {
          // make map of unique advizers
          acc[advizer.id] = advizer;
          return acc;
        }, {});

      const playlistsTheyWatched = Object.keys(
        result.playlistData?.playlists || {},
      ).map((playlistId) => {
        return playlistLibrary[playlistId];
      });

      const advizersTheyWatchedString = Object.values(
        advizersTheyWatched,
      ).reduce((acc: string, advizer, index: number) => {
        if (index < 5) {
          return (
            acc +
            "\n" +
            advizer.firstName +
            " - " +
            advizer.jobTitle +
            " @ " +
            advizer.publicCompanyName
          );

          return acc;
        }

        if (index === 5) {
          return acc + "\nand " + (advizersTheyWatched.length - 5) + " more...";
        }

        return acc;
      }, "");

      const playlistsTheyWatchedString = playlistsTheyWatched.reduce(
        (acc: string, playlist) => {
          const userPlaylistData =
            result.playlistData?.playlists?.[playlist.id];
          const clarityBefore =
            userPlaylistData?.steps?.["understanding-response-before"]?.value;
          const clarityAfter =
            userPlaylistData?.steps?.["understanding-response-after"]?.value;
          const percentChangeInUnderstanding =
            ((clarityAfter - clarityBefore) / clarityBefore) * 100;
          // console.log({ userPlaylistData });
          return (
            acc +
            "\n" +
            `${playlist.title}${isNaN(percentChangeInUnderstanding) ? " (not completed)" : ` (${percentChangeInUnderstanding > 0 ? "+" : ""}${percentChangeInUnderstanding.toFixed(0)}% change in understanding)`}`
          );
        },
        "",
      );

      let userExportData: Record<string, any> = {};

      if (exportType === "canvas") {
        userExportData = {
          Student: email,
          ID: email,
          "SIS User ID": "",
          "SIS Login ID": "",
          Section: "",
          "New Assignment": userAssignmentStatus === "completed" ? 1 : 0,
        };
      } else {
        userExportData = {
          "Exported At": `${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
          Email: email,
          Name: result.name || "",
          Status: formattedUserAssignmentStatusMap[userAssignmentStatus],
          ["Student Points"]: Number(
            userResultData.points.totalUserPoints.toFixed(2),
          ),
          ["Points Possible"]: Number(
            userResultData.points.totalPossiblePoints.toFixed(2),
          ),
          ["Percent of Points"]: Number(
            userResultData.points.userPercentOfPoints.toFixed(2),
          ),
          ["Total Minutes Watched"]: Number(
            userVideosWatched
              .reduce((acc, video) => acc + video.duration / 60, 0)
              .toFixed(2),
          ),
        };

        if (activeAssignment?.sessionSchedulingLink) {
          const responseDataForPrompt =
            result.responses?.["sessionSchedulingLink"];
          userExportData["Clicked session scheduling link"] =
            responseDataForPrompt
              ? new Date(responseDataForPrompt?.lastUpdatedAt).toLocaleString()
              : "";
        }

        userExportData["Advizers Watched"] = advizersTheyWatchedString;
        userExportData["Playlists Watched"] = playlistsTheyWatchedString;

        for (const prompt of Object.values(activeAssignment.prompts || {})) {
          const responseDataForPrompt = result.responses?.[prompt.id];

          userExportData[prompt.prompt || prompt.id || ""] =
            responseDataForPrompt?.response;
        }

        userExportData["First Video Watched At"] =
          !isNaN(firstVideoWatched) && firstVideoWatched > 0
            ? `${new Date(firstVideoWatched).toLocaleDateString()} ${new Date(firstVideoWatched).toLocaleTimeString()}`
            : "";
        userExportData["Last Video Watched At"] =
          !isNaN(mostRecentVideoWatched) && mostRecentVideoWatched > 0
            ? `${new Date(mostRecentVideoWatched).toLocaleDateString()} ${new Date(mostRecentVideoWatched).toLocaleTimeString()}`
            : "";
        userExportData["Last Assignment Prompt Response At"] =
          !isNaN(mostRecentAssignmentPromptResponse) &&
          mostRecentAssignmentPromptResponse > 0
            ? `${new Date(
                mostRecentAssignmentPromptResponse,
              ).toLocaleDateString()} ${new Date(
                mostRecentAssignmentPromptResponse,
              ).toLocaleTimeString()}`
            : "";
      }

      exportResultsMap[email] = userExportData;
    }

    let csv = ``;
    const headers = Object.keys(Object.values(exportResultsMap)[0] || {});
    const rows = Object.values(exportResultsMap).map((result) => {
      return Object.values(result).map((value) => {
        return escapeCsvValue(value);
      });
    });

    csv += headers.map((header) => escapeCsvValue(header)).join(",") + "\n";

    if (exportType === "canvas") {
      csv += ["Points Possible", "", "", "", "", 1].join(",") + "\n";
    }

    for (const row of rows) {
      csv += row.join(",") + "\n";
    }

    if (exportType === "canvas") {
      Analytics.assignmentExportCanvas({
        assignmentId,
        assignmentTitle: activeAssignment?.title,
      });
    } else {
      Analytics.assignmentExportAll({
        assignmentId,
        assignmentTitle: activeAssignment?.title,
      });
    }

    return csv;
  }

  calculateAssignmentScore() {}
}

export const AssignmentService = new AssignmentsService();

function escapeCsvValue(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = String(value);

  // Check if the value contains characters that need escaping
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    // Escape double quotes by doubling them and wrap in quotes
    const escapedValue = stringValue.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }

  return stringValue;
}
